// @flow
import React from 'react'
import * as d3 from 'd3-scale'
import { getMapserverHost } from 'catena/er2_map_userlayers/js/utils'
import ficusStyles from '../styles'
import type { SettingsBarType } from '../reducers/settings'
import type { FicusDataType, TransimDataType } from '../reducers/select_framework_bar'

export type PropsType = {
    ficusData: FicusDataType,
    hasAnalysisPanel: boolean,
    hasHeader: boolean,
    placement: string,
    settings: SettingsBarType,
    theme: ficusStyles,
    tocVisible: boolean,
}

function MapLegend(props: PropsType) {
    const hasFicusData = props.ficusData &&
        props.ficusData.themes.length &&
        props.ficusData.themes[0].mapfiles.length

    const legendPlacementStyles = {
        lowerLeft: {
            bottom: '30px',
            left: '10px',
        },
        lowerRight: {
            bottom: 0,
            right: '10px',
        },
        upperLeft: {
            top: '10px',
            left: '10px',
        },
        upperRight: {
            top: '120px',
            right: '10px',
        },
    }

    let legend
    const server = getMapserverHost()
    let legendSrc
    let legendStyle = {
        position: 'absolute',
        backgroundColor: `rgba(255, 255, 255, ${props.settings.popupOpacity})`,
        ...legendPlacementStyles[props.placement],
    }

    if (props.placement !== 'none') {
        if (props.ficusData.themeType === 'raster') {
            const md = props.ficusData.themes[0].mapfiles[0]
            legendSrc = `${server}/cgi-bin/mapserv?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetLegendGraphic&LAYER=${md.layer}&Format=image/png&map=${md.mapfile}&foo=${Math.random()}`
            legendStyle = {
                position: 'absolute',
                backgroundColor: `rgba(255, 255, 255, ${props.settings.popupOpacity})`,
                ...legendPlacementStyles[props.placement],
            }
            legend = (
                <img src={legendSrc} alt="legend" />
            )
        } else if (props.ficusData.themeType === 'vector' && props.ficusData.themes.transims.length) {
            let min = 100000
            let max = -100000
            props.ficusData.themes.transims.forEach((layerJSON) => {
                const { transimAttrs } = layerJSON
                transimAttrs.forEach((attr) => {
                    const vals = layerJSON.attrs.map(a => a.properties[attr]).filter(a => !isNaN(a))
                    min = Math.min(min, ...vals)
                    max = Math.max(max, ...vals)
                })
            })
            const meta = props.ficusData.themes.meta
            const range = meta.classification || ['red', 'yellow', 'green']
            const colorScale = d3.scaleLinear().domain([min, (max - min) / 2, max]).range(range)

            const nclasses = 5
            const step = (max - min) / (nclasses - 1)
            const classes = []
            for (let i = 0; i < nclasses; i += 1) {
                const val = min + (step * i)
                const color = colorScale(val)
                const classStyle = {
                    background: color,
                    border: '1px solid black',
                    display: 'inline-block',
                    height: '8px',
                    width: '14px',
                }
                const textStyle = {
                    fontFamily: 'Roboto Condensed',
                    fontSize: 'smaller',
                    padding: '3px',
                }
                classes.push((
                    <div key={val}><span style={classStyle} /><span style={textStyle}>{val.toFixed(3)}</span></div>
                ))
            }

            legend = (
                <span>
                    {classes}
                </span>
            )
        }
    } else {
        legendStyle = { display: 'none' }
    }

    const classNames = [props.theme.mapCtr, props.theme.mapToolbarPanelClosed]
    classNames.push(props.hasAnalysisPanel ? props.theme.analysisPanelOpen : props.theme.analysisPanelClosed)
    classNames.push(props.tocVisible ? props.theme.contextBarOpen : props.theme.contextBarClosed)
    classNames.push(props.hasHeader ? props.theme.headerPanelOpen : props.theme.headerPanelClosed)

    return (
        <div className={classNames.join(' ')} style={legendStyle}>
            {legend}
        </div>
    )
}

export default MapLegend
